import React from 'react';
import { MessageInfo, AtUserAttachment } from '@/api/types';
import AtUserTag from './AtUserTag';

interface TextMessageProps {
  message: MessageInfo;
  isOwnMessage: boolean;
}

/**
 * 解析消息内容，将 @ 用户替换为组件
 */
const parseMessageContent = (
  content: string,
  atUsers: AtUserAttachment[] | null | undefined,
  isOwnMessage: boolean
): React.ReactNode => {
  if (!atUsers || atUsers.length === 0) {
    return content;
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  
  // 为每个 @ 用户创建正则表达式
  const atPatterns = atUsers.map(user => ({
    user,
    pattern: new RegExp(`@${user.isAll ? '所有人' : user.name}`, 'g')
  }));

  // 找到所有 @ 匹配位置
  const matches: Array<{ index: number; length: number; user: AtUserAttachment }> = [];
  
  atPatterns.forEach(({ user, pattern }) => {
    let match;
    while ((match = pattern.exec(content)) !== null) {
      matches.push({
        index: match.index,
        length: match[0].length,
        user
      });
    }
  });

  // 按位置排序
  matches.sort((a, b) => a.index - b.index);

  // 构建结果
  matches.forEach((match, i) => {
    // 添加前面的文本
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }
    
    // 添加 @ 用户组件
    parts.push(
      <AtUserTag 
        key={`at-${i}-${match.user.id}`}
        atUser={match.user} 
        isOwnMessage={isOwnMessage} 
      />
    );
    
    lastIndex = match.index + match.length;
  });

  // 添加剩余文本
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return parts.length > 0 ? parts : content;
};

/**
 * 文本消息组件
 */
const TextMessage: React.FC<TextMessageProps> = ({ message, isOwnMessage }) => {
  const contentData = message.decryptedContentData;
  const hasTranslation = (contentData?.showTranslate && contentData?.translateContent) ||
    (contentData?.translateContent && contentData.content !== contentData?.translateContent);
  
  // 获取 @ 用户信息
  const atUsers = contentData?.attachment?.atUsers;

  return (
    <div className={`px-4 py-2 rounded-2xl max-w-xs md:max-w-md
     ${isOwnMessage ? 'bg-blue-500 text-white rounded-br-md' : 'bg-gray-100 text-gray-900 rounded-bl-md'}`}>
      {/* 原始消息内容 */}
      <div className="text-sm whitespace-pre-wrap break-words">
        {parseMessageContent(contentData?.content || '', atUsers, isOwnMessage)}
      </div>

      {/* 翻译内容 */}
      {hasTranslation && (
        <div className={`mt-2 pt-2 border-t ${isOwnMessage
          ? 'border-blue-400'
          : 'border-gray-300'
          }`}>
          <div className="flex items-center space-x-1 mb-1">
            <svg
              className={`w-3 h-3 ${isOwnMessage ? 'text-blue-200' : 'text-gray-500'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
            </svg>
            <span className={`text-xs ${isOwnMessage ? 'text-blue-200' : 'text-gray-500'}`}>
              翻译
            </span>
          </div>
          <div className={`text-sm whitespace-pre-wrap break-words ${isOwnMessage ? 'text-blue-100' : 'text-gray-700'}`}>
            {parseMessageContent(contentData?.translateContent || '', atUsers, isOwnMessage)}
          </div>
        </div>
      )}
    </div>
  );
};

export default TextMessage;
